import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { buildSchemaGraph } from "@/utils/schemas";

const defaultMeta = {
  title: "Overhaul Ops LLC | Firefighter-Owned Junk Removal DFW",
  description: "Professional junk removal, hauling, clean outs, and light demolition services in DFW. Firefighter owned & operated. Fully insured. Free quotes!",
  keywords: "junk removal DFW, junk removal Denton, junk removal Fort Worth, junk removal Tarrant County, junk removal Parker County, junk removal Wise County, hauling services Texas, clean out services DFW, light demolition Fort Worth, firefighter owned junk removal, estate cleanout DFW, garage cleanout, construction debris removal",
  image: "https://static.prod-images.emergentagent.com/jobs/102f95fb-a004-459d-9125-f5d93a968f04/images/99ba5e0ce457b0bb1f46be998264d47e147fbd5f00e18b4108592a5d2fa02d79.png",
  url: "https://overhaulops.com"
};

export default function SEOHead({
  title = "",
  description = "",
  keywords = "",
  image = "",
  type = "website",
  schemas = [],
  breadcrumbs = [],
}) {
  const location = useLocation();
  const { client_phone, client_email, google_analytics_id } = useSiteConfig();

  const pageTitle = title
    ? `${title} | Overhaul Ops LLC`
    : defaultMeta.title;
  const pageDescription = description || defaultMeta.description;
  const pageKeywords = keywords || defaultMeta.keywords;
  const pageImage = image || defaultMeta.image;
  const pageUrl = `${defaultMeta.url}${location.pathname}`;

  // Google Analytics
  useEffect(() => {
    if (google_analytics_id && !document.getElementById('ga-script')) {
      const script = document.createElement('script');
      script.id = 'ga-script';
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${google_analytics_id}`;
      document.head.appendChild(script);

      const inlineScript = document.createElement('script');
      inlineScript.id = 'ga-inline';
      inlineScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${google_analytics_id}');
      `;
      document.head.appendChild(inlineScript);
    }
  }, [google_analytics_id]);

  useEffect(() => {
    document.title = pageTitle;

    const updateMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attr}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    updateMeta('description', pageDescription);
    updateMeta('keywords', pageKeywords);
    updateMeta('og:title', pageTitle, true);
    updateMeta('og:description', pageDescription, true);
    updateMeta('og:image', pageImage, true);
    updateMeta('og:url', pageUrl, true);
    updateMeta('og:type', type, true);
    updateMeta('og:site_name', 'Overhaul Ops LLC', true);
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', pageTitle);
    updateMeta('twitter:description', pageDescription);
    updateMeta('twitter:image', pageImage);
    updateMeta('geo.region', 'US-TX');
    updateMeta('geo.placename', 'Dallas-Fort Worth');

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', pageUrl);

    // Build & inject JSON-LD schema graph
    const schemaGraph = buildSchemaGraph(
      schemas,
      breadcrumbs,
      { image: pageImage, phone: client_phone ? `+1-${client_phone.replace(/\D/g, '')}` : undefined }
    );

    // Remove all existing schema scripts and add fresh one
    document.querySelectorAll('script[data-schema="seo"]').forEach(el => el.remove());
    const schemaScript = document.createElement('script');
    schemaScript.setAttribute('type', 'application/ld+json');
    schemaScript.setAttribute('data-schema', 'seo');
    schemaScript.textContent = JSON.stringify(schemaGraph);
    document.head.appendChild(schemaScript);

  }, [pageTitle, pageDescription, pageKeywords, pageImage, pageUrl, type, schemas, breadcrumbs]);

  return null;
}
